// Rule-based Competitor Intelligence — pure business logic, ZERO AI calls.
// All calculations derived from Meta Ad Library extracted fields.

const SECONDS_PER_DAY = 86400;

/**
 * Estimate an ad's reach class when Meta doesn't expose reached_countries
 * in the public HTML payload (i.e. non-EU disclosure ads).
 *
 * Heuristic: assume `1` country unless the caller provides an explicit list.
 * We keep this conservative and honest — no fabricated numbers.
 */
export function normalizeCountries(ad) {
  if (Array.isArray(ad?.countries) && ad.countries.length > 0) {
    return ad.countries;
  }
  return []; // unknown — treated as < 2 for scoring
}

export function normalizePlatforms(ad) {
  const raw = Array.isArray(ad?.publisher_platforms) ? ad.publisher_platforms : [];
  return raw
    .map((p) => String(p).toUpperCase().replace(/[^A-Z_]/g, ""))
    .filter(Boolean);
}

/** Convert FB's SCREAMING_SNAKE_CASE platform token to a display label. */
export function platformLabel(token) {
  const map = {
    FACEBOOK: "Facebook",
    INSTAGRAM: "Instagram",
    MESSENGER: "Messenger",
    AUDIENCE_NETWORK: "Audience Network",
    THREADS: "Threads",
    WHATSAPP: "WhatsApp",
    OCULUS: "Meta Quest",
  };
  return map[token] || token.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function runningDays(ad, now = Date.now() / 1000) {
  const start = ad?.start_date_ts;
  if (!start) return null;
  return Math.max(0, Math.round((now - start) / SECONDS_PER_DAY));
}

// ────────────────────────────────────────────────────────────────
// CARD 1 · WINNING AD SCORE
// ────────────────────────────────────────────────────────────────
export function calculateWinningScore(ad, now = Date.now() / 1000) {
  const isActive = ad?.is_active === true;
  const days = runningDays(ad, now) ?? 0;
  const platforms = normalizePlatforms(ad);
  const countries = normalizeCountries(ad);

  let score = 0;
  const reasons = [];

  if (isActive) {
    score += 40;
    reasons.push({ ok: true, text: "Still Active" });
  } else {
    reasons.push({ ok: false, text: "Ad is not active" });
  }

  if (days >= 30) {
    score += 10;
    reasons.push({ ok: true, text: `Running for ${days} days` });
  }
  if (days >= 90) score += 20;
  if (days >= 180) score += 15;
  if (days >= 365) score += 15;

  if (countries.length > 1) {
    score += 5;
    reasons.push({ ok: true, text: `Running in ${countries.length} countries` });
  }
  if (countries.length > 5) score += 5;

  if (platforms.length > 1) {
    score += 5;
    reasons.push({
      ok: true,
      text: `Running on ${platforms.map(platformLabel).slice(0, 3).join(" + ")}${platforms.length > 3 ? ` +${platforms.length - 3}` : ""}`,
    });
  }

  score = Math.max(0, Math.min(100, score));

  let badge = "Low Confidence";
  let tone = "red";
  if (score >= 90) {
    badge = "Very Likely Winner";
    tone = "green";
  } else if (score >= 70) {
    badge = "Strong Performer";
    tone = "green";
  } else if (score >= 40) {
    badge = "Possible Winner";
    tone = "orange";
  }

  return { score, badge, tone, reasons, days, isActive };
}

// ────────────────────────────────────────────────────────────────
// CARD 2 · CAMPAIGN STAGE
// ────────────────────────────────────────────────────────────────
export function calculateCampaignStage(ad, now = Date.now() / 1000) {
  const days = runningDays(ad, now);
  const isActive = ad?.is_active === true;

  let stage = "Unknown";
  let icon = "🧪";
  let tone = "gray";

  if (days !== null) {
    if (days <= 14) {
      stage = "Testing";
      icon = "🧪";
      tone = "gray";
    } else if (days <= 45) {
      stage = "Validation";
      icon = "📈";
      tone = "orange";
    } else if (days <= 120) {
      stage = "Scaling";
      icon = "🚀";
      tone = "yellow";
    } else if (days <= 365) {
      stage = "Winning Campaign";
      icon = "💰";
      tone = "green";
    } else {
      stage = "Evergreen Winner";
      icon = "👑";
      tone = "green";
    }
  }

  const startedIso = ad?.start_date_ts
    ? new Date(ad.start_date_ts * 1000).toISOString()
    : null;

  return {
    stage,
    icon,
    tone,
    days,
    isActive,
    startedIso,
    statusLabel: isActive ? "Still Active" : "Inactive",
  };
}

// ────────────────────────────────────────────────────────────────
// CARD 3 · SCALE INDICATOR
// ────────────────────────────────────────────────────────────────
export function calculateScaleIndicator(ad) {
  const platforms = normalizePlatforms(ad);
  const countries = normalizeCountries(ad);

  // Country tier
  let countryClass = "Local Campaign";
  let countryIcon = "📍";
  if (countries.length >= 16) {
    countryClass = "Global Campaign";
    countryIcon = "🌍";
  } else if (countries.length >= 6) {
    countryClass = "International Campaign";
    countryIcon = "🌐";
  } else if (countries.length >= 2) {
    countryClass = "Regional Campaign";
    countryIcon = "🗺️";
  }

  // Platform tier (based on which are present)
  const hasFB = platforms.includes("FACEBOOK");
  const hasIG = platforms.includes("INSTAGRAM");
  const hasMSG = platforms.includes("MESSENGER");
  const hasAN = platforms.includes("AUDIENCE_NETWORK");

  let platformClass = "Basic";
  if (hasFB && hasIG && hasMSG && hasAN) platformClass = "Maximum Scale";
  else if (hasFB && hasIG && hasMSG) platformClass = "Scaled";
  else if (hasFB && hasIG) platformClass = "Growing";
  else if (platforms.length === 0) platformClass = "Unknown";

  // Confidence: prefer explicit country data + platform breadth
  let confidence = "Low";
  if (countries.length >= 2 && platforms.length >= 3) confidence = "High";
  else if (platforms.length >= 3) confidence = "Medium";
  else if (platforms.length >= 2) confidence = "Medium";

  return {
    countryClass,
    countryIcon,
    countryCount: countries.length,
    countriesKnown: countries.length > 0,
    platformClass,
    platforms,
    confidence,
  };
}

// ────────────────────────────────────────────────────────────────
// Composed teardown
// ────────────────────────────────────────────────────────────────
export function buildCompetitorIntelligence(ad, now = Date.now() / 1000) {
  const winning = calculateWinningScore(ad, now);
  const stage = calculateCampaignStage(ad, now);
  const scale = calculateScaleIndicator(ad);

  const parts = [];
  if (winning.days > 0) {
    parts.push(
      `This ad has been active for ${winning.days} day${winning.days === 1 ? "" : "s"} and is ${winning.isActive ? "still running" : "no longer running"}.`
    );
  }
  if (scale.platforms.length > 0) {
    const platformStr =
      scale.platforms.length === 1
        ? platformLabel(scale.platforms[0])
        : `${scale.platforms.length} Meta platforms`;
    if (scale.countriesKnown) {
      parts.push(
        `It is running across ${scale.countryCount} countr${scale.countryCount === 1 ? "y" : "ies"} on ${platformStr}.`
      );
    } else {
      parts.push(`It is delivered on ${platformStr}.`);
    }
  }
  parts.push(
    `Based on the rule-based scoring system, this ad has a Winning Ad Score of ${winning.score}/100 and is classified as a ${winning.badge}.`
  );
  const summary = parts.join(" ");

  return {
    winningScore: winning.score,
    winningBadge: winning.badge,
    winningTone: winning.tone,
    winningReasons: winning.reasons,
    campaignStage: stage.stage,
    campaignIcon: stage.icon,
    campaignTone: stage.tone,
    runningDays: winning.days,
    startedIso: stage.startedIso,
    isActive: winning.isActive,
    statusLabel: stage.statusLabel,
    scaleIndicator: scale.countryClass,
    scaleIcon: scale.countryIcon,
    countryCount: scale.countryCount,
    countriesKnown: scale.countriesKnown,
    platformClass: scale.platformClass,
    platforms: scale.platforms,
    confidence: scale.confidence,
    summary,
  };
}
